/**
 * Locale: Bahasa Indonesia first, English at parity.
 *
 * Two rules shape this file:
 *
 * 1. **`id` is the default, not the fallback.** Suap is built for Indonesia, so
 *    an unrecognised browser language resolves to Bahasa rather than to English.
 *    A product whose default drifts to English is a product that treats its
 *    primary market as the edge case.
 * 2. **The resolved locale is stamped onto `<html lang>`.** The api client sends
 *    `X-Suap-Language` from `document.documentElement.lang` (SUAP-0101) and the
 *    backend honours it over `Accept-Language` (SUAP-0005) — so if this file and
 *    that attribute disagree, the screen is in one language and the error
 *    messages are in another. That is not hypothetical: SUAP-0101's own
 *    verification hit it when a browser extension rewrote `lang`.
 *
 * The dictionary is a plain object rather than a loader: at this size, lazy
 * loading a locale would add a suspense boundary and a loading state to save a
 * few kilobytes, and both are worse than the kilobytes.
 *
 * i18n-exempt: this file IS the dictionary — its strings are the translations,
 * so check:i18n skips it rather than flagging every entry as a bare literal.
 *
 * SUAP-0110.
 */
import { createContext, useContext } from 'react'

export type Locale = 'id' | 'en'

export const LOCALES: Locale[] = ['id', 'en']
export const DEFAULT_LOCALE: Locale = 'id'
const STORAGE_KEY = 'suap.locale'

/**
 * Every user-visible string in the app.
 *
 * Keys are `area.thing`, and both locales are required — `check:i18n` fails the
 * build on a half-translated entry, because a missing translation that falls
 * back silently is a missing translation nobody finds.
 */
export const STRINGS = {
  'nav.today': { id: 'Hari ini', en: 'Today' },
  'nav.diary': { id: 'Catatan', en: 'Diary' },
  'nav.fasting': { id: 'Puasa', en: 'Fasting' },
  'nav.me': { id: 'Saya', en: 'Me' },
  'nav.log': { id: 'Catat makan', en: 'Log a meal' },
  'nav.main': { id: 'Navigasi utama', en: 'Main navigation' },

  'action.back': { id: 'Kembali', en: 'Back' },
  'action.close': { id: 'Tutup', en: 'Close' },
  'action.retry': { id: 'Coba lagi', en: 'Try again' },
  'action.home': { id: 'Ke Hari ini', en: 'Go to Today' },

  'state.loading': { id: 'Memuat', en: 'Loading' },
  'state.comingSoon': { id: 'Layar ini menyusul di epic-nya.', en: 'This screen arrives with its epic.' },

  'error.title': { id: 'Ada yang tidak beres', en: 'Something went wrong' },
  'error.body': {
    id: 'Coba muat ulang. Kalau masih sama, catatanmu tetap aman.',
    en: 'Try reloading. If it keeps happening, your log is still safe.',
  },

  'notFound.title': { id: 'Halaman tidak ditemukan', en: 'Page not found' },
  // The Bahasa is SUAP-0104's, restored. SUAP-0110 was a *migration* ticket — move
  // every shipped string into the dictionary — and it rewrote this sentence instead
  // of translating it ('Tautan ini tidak ada, atau sudah pindah.' became 'Alamat itu
  // tidak ada di Suap.', which drops the "or has moved" half). A copy change is a
  // copy decision and wants its own ticket; restoring it is cheaper than arguing it.
  'notFound.body': {
    id: 'Tautan ini tidak ada, atau sudah pindah.',
    en: 'This link does not exist, or has moved.',
  },

  // Not specimen data, though it renders on /ui-kit next to the placeholder
  // mascot: it is the product's stated position on what the mascot is for, and
  // CLAUDE.md non-negotiable 6 — streaks and encouragement reward logging, never
  // restriction — is what it is quoting. It sat in UiKit.tsx as a bare Bahasa
  // literal, exempt because the whole file was, and rendered untranslated on the
  // English UI. SUAP-0112.
  'mascot.policy': {
    id: 'Maskot memberi semangat untuk mencatat, tidak pernah untuk menahan makan, dan tidak pernah berkomentar soal tubuh.',
    en: 'The mascot encourages you to log, never to eat less, and never comments on your body.',
  },

  // Interpolated, not concatenated. `${t('stepper.decrease')} ${label}` put the
  // verb and the noun in a fixed order set by the *code* rather than by either
  // language, and it read "Decrease Porsi" the moment the two halves came from
  // different places. A composed accessible name has to be one dictionary entry,
  // so each locale owns its own word order. SUAP-0110 third review.
  'stepper.increase': { id: 'Tambah {label}', en: 'Increase {label}' },
  'stepper.decrease': { id: 'Kurangi {label}', en: 'Decrease {label}' },

  // ── Auth (SUAP-0202) ──────────────────────────────────────────────────
  //
  // Descriptive, never evaluative, and never blaming: "email atau kata sandi
  // salah" says what is wrong without telling the user they got it wrong.
  'auth.signIn.title': { id: 'Masuk', en: 'Sign in' },
  'auth.signIn.action': { id: 'Masuk', en: 'Sign in' },
  'auth.signIn.switch': { id: 'Belum punya akun?', en: 'No account yet?' },
  'auth.signIn.switchAction': { id: 'Daftar', en: 'Create one' },

  'auth.register.title': { id: 'Daftar', en: 'Create an account' },
  'auth.register.action': { id: 'Daftar', en: 'Create account' },
  'auth.register.switch': { id: 'Sudah punya akun?', en: 'Already have an account?' },
  'auth.register.switchAction': { id: 'Masuk', en: 'Sign in' },

  'auth.email': { id: 'Email', en: 'Email' },
  'auth.password': { id: 'Kata sandi', en: 'Password' },
  'auth.password.hint': { id: 'Minimal 8 karakter.', en: 'At least 8 characters.' },

  // Field-level, so they render against the input that caused them.
  'auth.error.email.required': { id: 'Isi emailmu.', en: 'Enter your email.' },
  'auth.error.email.invalid': { id: 'Sepertinya ini bukan alamat email.', en: 'That does not look like an email address.' },
  'auth.error.password.required': { id: 'Isi kata sandimu.', en: 'Enter your password.' },
  'auth.error.password.short': { id: 'Minimal 8 karakter.', en: 'At least 8 characters.' },

  // Form-level, mapped from error.code.
  'auth.error.invalidCredentials': {
    id: 'Email atau kata sandi tidak cocok.',
    en: 'That email and password do not match.',
  },
  'auth.error.emailTaken': {
    id: 'Email ini sudah terdaftar. Coba masuk.',
    en: 'That email is already registered. Try signing in.',
  },
  'auth.error.rateLimited': {
    id: 'Terlalu banyak percobaan. Tunggu sebentar, lalu coba lagi.',
    en: 'Too many attempts. Wait a moment, then try again.',
  },

  // Shown once, at the top of the sign-in screen, when the session ended without
  // the user asking. Says what happened and what to do — not "error".
  'auth.signedOut.revoked': {
    id: 'Sesimu berakhir karena kata sandi diubah di perangkat lain. Masuk lagi untuk melanjutkan.',
    en: 'Your session ended because the password was changed on another device. Sign in again to continue.',
  },

  // ── Onboarding wizard shell (SUAP-0204) ───────────────────────────────
  //
  // The shell's copy only. Each step's own questions belong to its ticket
  // (SUAP-0205–0207, SUAP-1210) and the plan preview's to SUAP-0210 — the
  // `.why` lines below are the shell's promise that every step says, in one
  // line, what it is for. That is an acceptance criterion, not decoration: a
  // question a user cannot see the point of is a question they abandon.
  //
  // Slugs are the join key with the server, which owns the step list. A key
  // here with no step is dead copy; a step with no key renders untranslated,
  // and check:i18n would not catch it because the slug is data, not a literal.
  'onboarding.title': { id: 'Kenalan dulu', en: 'Let us get to know you' },
  'onboarding.progress': { id: 'Langkah {current} dari {total}', en: 'Step {current} of {total}' },
  'onboarding.progress.label': { id: 'Kemajuan pengisian', en: 'Onboarding progress' },

  'onboarding.step.body.title': { id: 'Tubuhmu', en: 'Your body' },
  'onboarding.step.body.why': {
    id: 'Jenis kelamin, tanggal lahir dan tinggi badan dipakai untuk menghitung kebutuhan energi istirahatmu.',
    en: 'Sex at birth, date of birth and height are what your resting energy needs are calculated from.',
  },
  'onboarding.step.activity.title': { id: 'Aktivitasmu', en: 'Your activity' },
  // **This line carries a number from docs/SCIENCE.md, and it is the only one on
  // this screen that does.** §1.2: placing a person on the PAL scale by one
  // self-report question is grade C, and TDEE₀ is a
  // "cold-start seed only, valid for ~14 days, then discard it (§1.3)". "Setelah
  // sekitar dua minggu" / "after about two weeks" is that ~14 days reaching a
  // user, so SUAP-0204's frontmatter carries the anchor. Shipped under
  // `science: []` and caught at review — CLAUDE.md § Working here rule 3.
  //
  // If this interval ever changes it changes in §1.2 first, and this string
  // follows. It is not a rounding of the engine's behaviour; there is no engine
  // yet (SUAP-0602 seeds it, SUAP-0604 replaces it).
  'onboarding.step.activity.why': {
    id: 'Ini perkiraan awal saja. Setelah sekitar dua minggu, Suap memakai data energimu sendiri.',
    en: 'This is only a starting estimate. After about two weeks, Suap uses your own energy data instead.',
  },
  'onboarding.step.goal.title': { id: 'Targetmu', en: 'Your goal' },
  'onboarding.step.goal.why': {
    id: 'Arah dan kecepatan perubahan menentukan target kalori harianmu.',
    en: 'Direction and pace are what set your daily calorie target.',
  },
  'onboarding.step.schedule.title': { id: 'Jadwal makanmu', en: 'Your eating schedule' },
  'onboarding.step.schedule.why': {
    id: 'Dipakai untuk mengatur waktu pengingat. Boleh dilewati — nanti bisa diubah.',
    en: 'Used to time reminders. You can skip this and change it later.',
  },
  'onboarding.step.health.title': { id: 'Skrining kesehatan', en: 'Health screen' },
  'onboarding.step.health.why': {
    id: 'Beberapa kondisi membuat puasa tidak aman. Suap menanyakannya sebelum menawarkan fitur itu.',
    en: 'Some conditions make fasting unsafe. Suap asks before offering the feature.',
  },
  'onboarding.step.plan.title': { id: 'Rencanamu', en: 'Your plan' },
  'onboarding.step.plan.why': {
    id: 'Ringkasan dari semua jawabanmu.',
    en: 'A summary of everything you answered.',
  },

  // The step bodies are placeholders until their tickets land. Said plainly,
  // because a blank panel reads as a bug and this is not one.
  'onboarding.step.pending': {
    id: 'Pertanyaan langkah ini menyusul di tiketnya.',
    en: 'This step\u2019s questions arrive with its ticket.',
  },

  'onboarding.action.next': { id: 'Lanjut', en: 'Continue' },
  // Not 'Kembali'. The pushed-route chrome (SUAP-0104) already has a back arrow
  // whose accessible name is `action.back` — 'Kembali' — and it leaves the
  // wizard. Two controls one step apart, both called Back, doing different
  // things: `ui:drive:onboarding` hit the ambiguity as a strict-mode violation,
  // and a user would have hit it as losing their place. This one names what it
  // goes back *to*.
  'onboarding.action.back': { id: 'Langkah sebelumnya', en: 'Previous step' },
  'onboarding.action.skip': { id: 'Lewati', en: 'Skip' },
  'onboarding.action.finish': { id: 'Selesai', en: 'Finish' },

  'onboarding.done.title': { id: 'Semua sudah terisi', en: 'That is everything' },
  'onboarding.done.body': {
    id: 'Jawabanmu tersimpan. Kamu bisa mengubahnya kapan saja di Saya.',
    en: 'Your answers are saved. You can change them any time under Me.',
  },

  // Saving is a network round trip on every step, so the failure needs copy.
  // Descriptive and not blaming, like the auth errors above.
  'onboarding.error.save': {
    id: 'Jawabanmu belum tersimpan. Coba lagi.',
    en: 'That did not save. Try again.',
  },

  'screen.onboarding': { id: 'Kenalan dulu', en: 'Let us get to know you' },

  'screen.signIn': { id: 'Masuk', en: 'Sign in' },
  'screen.register': { id: 'Daftar', en: 'Create an account' },

  'screen.today': { id: 'Hari ini', en: 'Today' },
  'screen.diary': { id: 'Catatan', en: 'Diary' },
  'screen.fasting': { id: 'Puasa', en: 'Fasting' },
  'screen.me': { id: 'Saya', en: 'Me' },
  'screen.log': { id: 'Catat makan', en: 'Log a meal' },
  'screen.install': { id: 'Pasang Suap', en: 'Install Suap' },
  'screen.uiKit': { id: 'UI Kit', en: 'UI Kit' },

  // SUAP-1202 — the fasting gate. docs/SCIENCE.md §3.4: "a blocked user still
  // gets the full calorie/macro/micro product. We remove the fasting feature,
  // not the person. Copy must never read as a rejection."
  //
  // Every string below names the **situation**, never the person, and none of
  // them apologises. "Belum bisa dipakai" — not yet usable — rather than "kamu
  // tidak memenuhi syarat", which is a sentence about the reader.
  'gate.reason.notScreened': {
    id: 'Jadwal puasa aktif setelah kamu mengisi skrining kesehatan.',
    en: 'Fasting schedules become available once you complete the health screen.',
  },
  'gate.reason.blocked': {
    id: 'Fitur puasa tidak ditampilkan untuk kondisi berikut.',
    en: 'Fasting is not offered alongside the following.',
  },
  'gate.reason.needsAttestation': {
    id: 'Fitur puasa perlu persetujuan dokter untuk kondisi berikut.',
    en: 'Fasting needs a clinician’s approval alongside the following.',
  },

  'gate.pregnancy': { id: 'Sedang hamil.', en: 'Pregnancy.' },
  'gate.breastfeeding': { id: 'Sedang menyusui.', en: 'Breastfeeding.' },
  // Covers **both** readings of `fasting_blocked_under_18`, because the gate
  // does: model.Body blocks on a *known* age under 18 and on an *unknown* age,
  // deliberately — "we do not know how old you are" must not open a gate that
  // exists to keep children out of it. One gate id, two situations, and copy
  // that said only "under 18" told a forty-year-old with an empty birth date
  // something false about themselves. Found by rendering the screen.
  //
  // The better fix is a distinct gate for "age unknown" so the two can be
  // explained apart — recorded in docs/coverage/EPIC-12.md § SUAP-1202 as an
  // open item rather than done quietly here, because it changes a value
  // SUAP-1201's versioned question set produces.
  'gate.under18': {
    id: 'Usia di bawah 18 tahun, atau tanggal lahir belum terisi.',
    en: 'Under 18, or no birth date on file.',
  },
  'gate.edHistory': {
    id: 'Riwayat gangguan makan.',
    en: 'A history of disordered eating.',
  },
  'gate.underweight': { id: 'Berat badan di bawah rentang sehat.', en: 'Weight below the healthy range.' },
  'gate.type1': { id: 'Diabetes tipe 1.', en: 'Type 1 diabetes.' },
  'gate.hypoRisk': {
    id: 'Penggunaan insulin atau sulfonilurea — dosis mungkin perlu disesuaikan.',
    en: 'Insulin or sulfonylurea use — the dose may need adjusting.',
  },
  'gate.clinician': {
    id: 'Kondisi yang sedang ditangani dokter.',
    en: 'A condition under a clinician’s care.',
  },
  'gate.unknown': {
    id: 'Kondisi yang tercatat pada skrining kesehatanmu.',
    en: 'A condition recorded on your health screen.',
  },

  // SUAP-1214 — the attestation action. docs/SCIENCE.md §3.4 gates four conditions
  // rather than blocking them: "behind an explicit 'my clinician approved this'
  // attestation + hypoglycaemia warning".
  //
  // The wording is the difficult part. It has to be an assertion the person makes
  // about a conversation that already happened — not a dare, and not a checkbox
  // to click past. "Dokter saya sudah menyetujui" is past tense and first person
  // on purpose: the app is recording something, not granting something.
  'gate.attest.action': {
    id: 'Dokter saya sudah menyetujui ini',
    en: 'My clinician has approved this',
  },
  'gate.attest.failed': {
    id: 'Belum tersimpan. Coba lagi sebentar lagi.',
    en: 'That did not save. Try again in a moment.',
  },
  'gate.attested.state': {
    id: 'Kamu menyatakan doktermu sudah menyetujui ini.',
    en: 'You stated that your clinician approved this.',
  },
  'gate.attested.heading': {
    id: 'Persetujuan dokter',
    en: 'Clinician approvals',
  },
  'gate.attested.withdrawNote': {
    id: 'Kalau dibatalkan, fitur puasa berhenti tersedia lagi. Fitur lain tidak berubah.',
    en: 'Withdrawing makes fasting unavailable again. Nothing else changes.',
  },
  'gate.attested.withdraw': {
    id: 'Batalkan pernyataan ini',
    en: 'Withdraw this statement',
  },

  // §3.4 asks for a hypoglycaemia warning *alongside* the attestation for the
  // insulin/sulfonylurea row, and only that row. It says what can happen and what
  // to do about it, and it does not diagnose — Suap is not medical advice, and the
  // action it points at is the clinician, not the app.
  'gate.hypoRisk.warning': {
    id: 'Puasa bersama insulin atau sulfonilurea bisa menurunkan gula darah terlalu jauh. Dosis biasanya perlu disesuaikan lebih dulu — bicarakan dengan dokter sebelum mulai.',
    en: 'Fasting alongside insulin or a sulfonylurea can drop blood sugar too far. The dose usually needs adjusting first — talk to your clinician before you start.',
  },

  // The half that keeps this a screen rather than a refusal.
  'gate.stillAvailable': {
    id: 'Semua fitur lain tetap jalan: target kalori, makro, mikronutrien, catatan makan dan berat badan.',
    en: 'Everything else keeps working: your calorie target, macros, micronutrients, food diary and weigh-ins.',
  },

  'error.offline': {
    id: 'Tidak ada koneksi. Coba lagi setelah tersambung.',
    en: 'No connection. Try again once you are back online.',
  },
  'error.server': {
    id: 'Ada gangguan di sisi kami. Coba lagi sebentar lagi.',
    en: 'Something went wrong on our side. Try again shortly.',
  },
  'error.emptyResponse': {
    id: 'Respons kosong dari server.',
    en: 'The server returned an empty response.',
  },
  'bar.ofTarget': { id: '{value} dari target {target}', en: '{value} of {target} target' },
  // Ring carried the same untranslated-aria-label bug as Bar and was missed when
  // Bar's was fixed, because the old guard read the bare "dari" as a token rather
  // than as copy. SUAP-0110 review.
  'ring.ofTarget': { id: '{value} dari {target}', en: '{value} of {target}' },
  'ring.overTarget': { id: ', {percent}% di atas target', en: ', {percent}% over target' },

  'install.title': { id: 'Pasang Suap', en: 'Install Suap' },
  // The third clause promised that your log survives going offline. The same
  // commit moved that half of AC-5 to SUAP-0401 and cached nothing under /api,
  // so the copy was advertising a feature the ticket had just descoped. The
  // amendment's whole virtue was being honest rather than silent. SUAP-0106 review.
  'install.why': {
    id: 'Dipasang, Suap terbuka dari layar utama dan tetap jalan tanpa sinyal.',
    en: 'Installed, Suap opens from your home screen and still works without a signal.',
  },
  'install.action': { id: 'Pasang sekarang', en: 'Install now' },
  'install.installed': { id: 'Suap sudah terpasang di perangkat ini.', en: 'Suap is already installed on this device.' },
  'install.iosTitle': { id: 'Di iPhone dan iPad', en: 'On iPhone and iPad' },
  'install.iosStep1': { id: 'Ketuk tombol Bagikan di bilah Safari.', en: 'Tap the Share button in Safari.' },
  'install.iosStep2': { id: 'Pilih Tambahkan ke Layar Utama.', en: 'Choose Add to Home Screen.' },
  'install.iosStep3': { id: 'Ketuk Tambah, lalu buka Suap dari layar utama.', en: 'Tap Add, then open Suap from your home screen.' },
  'install.iosNote': { id: 'Safari tidak punya tombol pasang, jadi langkahnya manual. Ditulis untuk iOS 17 dan 18.', en: 'Safari has no install button, so these steps are manual. Written for iOS 17 and 18.' },
  'install.desktopTitle': { id: 'Di komputer', en: 'On a computer' },
  'install.desktopBody': {
    id: 'Suap dirancang untuk layar ponsel, jadi di komputer tampilannya tetap selebar ponsel dan diletakkan di tengah. Itu disengaja, bukan tampilan yang belum jadi.',
    en: 'Suap is built for phone screens, so on a computer it stays phone-width and centred. That is deliberate, not an unfinished layout.',
  },
  'install.unsupported': { id: 'Browser ini belum bisa memasang aplikasi web. Coba Chrome di Android, atau Safari di iPhone.', en: 'This browser cannot install web apps yet. Try Chrome on Android, or Safari on iPhone.' },
  // Describes the state the user is in, not a browser policy this repo cannot
  // evidence. The first version said "you can try again any time" (false: the
  // button is gone), and the second explained that "Chrome hides its install
  // offer for a while" — a mechanism nothing here measures, in the ticket that
  // existed because the previous copy asserted a mechanism nothing measured.
  // Reload and a still-installable origin fires a fresh event, so even the
  // duration was doubtful.
  //
  // No three-dots glyph: U+22EE is outside both declared unicode-ranges in
  // index.css (the character is deliberately not written here either), so
  // it can never come from Plus Jakarta Sans and would render from a system font
  // or as tofu. That is the exact trap check-fonts.mjs documents for U+02BF —
  // it looked fine on macOS because a system font supplied it. Named in words
  // instead, which also survives Edge and Samsung Internet, where the menu is
  // not three dots. Both found in review. SUAP-0114.
  // SUAP-0116 took the state claim off the front of both halves. It opened with
  // “Belum jadi dipasang” / “Not installed”, and this page cannot know that: a
  // browser tab reads `display-mode: standalone` as false for an app that is on
  // the home screen, because that is a property of the window and not of the
  // origin. So the copy now describes what the page *did* observe — a dismissal,
  // and the button going with it — and closes with the neutral sentence the
  // ticket asked for, which costs nothing if the app is not installed and is the
  // whole answer if it is. No new codepoints: `check:fonts` proves every glyph
  // here comes from the webfont, and an em dash would have been a new one.
  'install.dismissed': {
    id: 'Tombol pasang tidak muncul lagi sekarang. Buka menu browser, lalu pilih “Instal aplikasi” atau “Tambahkan ke Layar utama”. Kalau Suap sudah ada di layar utama, buka saja dari sana.',
    en: 'The install button is gone for now. Open your browser’s menu and choose “Install app” or “Add to Home screen”. If Suap is already on your home screen, open it from there.',
  },

  'update.available': { id: 'Versi baru Suap sudah siap.', en: 'A new version of Suap is ready.' },
  'update.action': { id: 'Muat ulang', en: 'Reload' },

  'mascot.idle': { id: 'Maskot Suap', en: 'The Suap mascot' },
  'mascot.happy': { id: 'Maskot Suap tersenyum', en: 'The Suap mascot, smiling' },
  'mascot.thinking': { id: 'Maskot Suap sedang berpikir', en: 'The Suap mascot, thinking' },
  'mascot.resting': { id: 'Maskot Suap sedang istirahat', en: 'The Suap mascot, resting' },
  'mascot.curious': { id: 'Maskot Suap penasaran', en: 'The Suap mascot, curious' },

  'locale.switch': { id: 'English', en: 'Bahasa' },
  'locale.label': { id: 'Bahasa', en: 'Language' },

  /*
   * The labels `/ui-kit` hands to the shipped primitives.
   *
   * These are here because `check:i18n` stopped letting the kit's file-level
   * exemption cover a primitive's accessible name: `<Bar label="Karbohidrat">`
   * becomes an `aria-label` inside `src/ui/Bar.tsx`, and in English the kit was
   * serving that Bahasa label beside an English `aria-valuetext` on the same
   * element. SUAP-0110 third review.
   *
   * They are field labels and macro names rather than invented copy — but the
   * *wording* on a real screen belongs to the ticket that builds it: EPIC-02 owns
   * the profile fields, EPIC-06 the goal selector, EPIC-07 the macro readouts. A
   * later ticket rewording one of these is doing its job, not breaking this one.
   */
  'macro.protein': { id: 'Protein', en: 'Protein' },
  'macro.carbs': { id: 'Karbohidrat', en: 'Carbs' },
  'macro.fat': { id: 'Lemak', en: 'Fat' },
  'macro.fibre': { id: 'Serat', en: 'Fibre' },

  'field.portion': { id: 'Porsi', en: 'Portion' },
  'field.weight': { id: 'Berat', en: 'Weight' },
  'field.email': { id: 'Email', en: 'Email' },
  'field.emailExample': { id: 'kamu@contoh.com', en: 'you@example.com' },
  'field.name': { id: 'Nama', en: 'Name' },
  'field.nameHint': { id: 'Nama panggilan cukup.', en: 'A nickname is enough.' },
  'field.password': { id: 'Kata sandi', en: 'Password' },
  'field.passwordTooShort': { id: 'Minimal 8 karakter.', en: 'At least 8 characters.' },
  'field.note': { id: 'Catatan', en: 'Note' },
  'field.optional': { id: 'Opsional', en: 'Optional' },
  'field.decimalHint': {
    id: 'Koma atau titik, keduanya jalan.',
    en: 'A comma or a full stop, either works.',
  },

  'goal.label': { id: 'Tujuan', en: 'Goal' },
  'goal.down': { id: 'Turun', en: 'Lose' },
  'goal.hold': { id: 'Jaga', en: 'Maintain' },
  'goal.up': { id: 'Naik', en: 'Gain' },

  'sheet.editPortion': { id: 'Ubah porsi', en: 'Edit portion' },

  'diary.emptyTitle': { id: 'Belum ada catatan hari ini', en: 'Nothing logged today' },
  'diary.emptyBody': {
    id: 'Catat sarapan untuk memulai. Butuh lima belas detik.',
    en: 'Log breakfast to start. It takes fifteen seconds.',
  },
} as const satisfies Record<string, Record<Locale, string>>

export type StringId = keyof typeof STRINGS

function isLocale(value: unknown): value is Locale {
  return value === 'id' || value === 'en'
}

/**
 * The stored preference, then the browser's, then Bahasa.
 *
 * Defended the same way `theme.ts` is: storage throws in a locked-down context,
 * and a language is not worth a white screen.
 */
export function resolveLocale(): Locale {
  try {
    const stored: unknown = window.localStorage.getItem(STORAGE_KEY)
    if (isLocale(stored)) return stored
  } catch {
    // fall through to the browser, then to the default
  }

  try {
    for (const tag of window.navigator.languages ?? [window.navigator.language]) {
      const base = tag.toLowerCase().split('-')[0]
      // Indonesian is `id`; `in` is the deprecated ISO 639 code some Android
      // builds still send, and dropping it would send those users to English.
      if (base === 'id' || base === 'in') return 'id'
      if (base === 'en') return 'en'
    }
  } catch {
    // no navigator (SSR, a test harness): the default is correct
  }

  return DEFAULT_LOCALE
}

export function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // The session keeps the choice; the next load falls back to resolution.
  }
}

/** Stamp the locale where the api client reads it. */
export function applyLocale(locale: Locale): void {
  document.documentElement.lang = locale
}

/**
 * Look a string up, filling {placeholders}.
 *
 * Exported for the few places that cannot use the hook — the error boundary,
 * which must render when the provider may have failed, and the api client, which
 * is not a component at all.
 */
export function translate(locale: Locale, key: StringId, vars?: Record<string, string | number>): string {
  const template = STRINGS[key][locale]
  if (vars === undefined) return template
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => String(vars[name] ?? whole))
}

export interface I18n {
  locale: Locale
  t: (key: StringId, vars?: Record<string, string | number>) => string
  setLocale: (locale: Locale) => void
}

export const I18nContext = createContext<I18n | null>(null)

/**
 * The translation hook.
 *
 * Throws without a provider rather than falling back to Bahasa: a component
 * rendering outside the provider would silently ignore the user's choice, and
 * that is a bug worth finding in development.
 */
export function useT(): I18n {
  const value = useContext(I18nContext)
  if (value === null) throw new Error('useT must be used inside <I18nProvider>')
  return value
}
